import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Više izbora koji zajedno čine jedno stanje ekrana, u adresi, i sa JEDNIM upisivačem.
 *
 * Filteri spiska nisu nezavisni jedan od drugog: menjaju se u grupi (dugme „očisti" skida sve
 * odjednom) i svaki od njih obesmišljava stranu. Zato ovde ne stoji po jedan hook za svaki ključ
 * nego jedan zapis i jedna izmena nad njim.
 *
 * **Zašto ne hook po ključu.** `setSearchParams` u react-router-u 7 gradi novu adresu iz parametara
 * TEKUĆEG iscrtavanja (`nextInit(new URLSearchParams(searchParams))`, gde `searchParams` visi o
 * `location.search`). Četiri poziva u istom potezu zato čitaju istu osnovu i **poslednji pregazi
 * prethodna tri** — brisanje filtera bi obrisalo samo jedan. Isti razlog stoji uz `useUrlParam`,
 * koji zbog njega prima spisak parametara koje izmena poništava.
 *
 * Prazna vrednost se iz adrese briše, ne piše kao `?country=`: prazan filter nije filter.
 * Izmena ne upisuje novu stavku u istoriju (`replace`), pa kucanje u polje za pretragu ne znači
 * dvadeset pritisaka na „nazad" da bi se izašlo sa spiska.
 */
export function useUrlZapis<K extends string>(
  kljucevi: readonly K[],
  ponisti: readonly string[] = [],
): [Record<K, string>, (izmene: Partial<Record<K, string>>) => void] {
  const [params, setParams] = useSearchParams();

  const imena = kljucevi.join(",");
  const zapis = useMemo(() => {
    const out = {} as Record<K, string>;
    for (const k of imena === "" ? [] : (imena.split(",") as K[])) out[k] = params.get(k) ?? "";
    return out;
  }, [imena, params]);

  const ponistiImena = ponisti.join(",");
  const izmeni = useCallback(
    (izmene: Partial<Record<K, string>>) => {
      setParams(
        (prethodni) => {
          const sledeci = new URLSearchParams(prethodni);
          for (const k of Object.keys(izmene) as K[]) {
            const v = izmene[k];
            if (v === undefined || v === "") sledeci.delete(k);
            else sledeci.set(k, v);
          }
          for (const k of ponistiImena === "" ? [] : ponistiImena.split(",")) sledeci.delete(k);
          return sledeci;
        },
        { replace: true },
      );
    },
    [ponistiImena, setParams],
  );

  return [zapis, izmeni];
}
